/* Source: DEV-448 Virtual Bed Designer | 2026-10-03 | live mix-and-match bed generator for Paseo Road.
 * Builds the layer steps from the render server's /catalog, asks the server to draw the chosen bed (a cached
 * combination returns at once; a new one streams progress for about 30 seconds after a Cloudflare Turnstile check),
 * and adds the pieces to the cart. Cart adds follow assets/complete-set.js: one /cart/add.js request, then a bubbling
 * `cart:item-added` event so the theme's existing bridge refreshes the cart count and opens the drawer.
 * Every line carries the hidden property `_bed_designer` (underscore properties never render in the cart). */

const PR_BD_SLOTS = [
  { slot: 'base', kind: 'cover', label: 'Foundation', title: 'Find Your Foundation', help: 'Choose the bedding that covers your bed. Matching shams are included.', optional: false, qty: 1 },
  { slot: 'top', kind: 'cover', label: 'Layering Piece', title: 'Add a Layer of Texture', help: 'Quilts and coverlets make an easy folded layer. Comforters and duvets add loft. Bedspreads stay in the Foundation.', optional: true, qty: 1 },
  { slot: 'euro', kind: 'euro', label: 'Euro Shams', title: 'Build Your Back Row', help: 'Euro shams bring height and softness: two for Queen, three for King. Sham inserts are separate unless included on the product page.', optional: true, qty: 2 },
  { slot: 'lumbar', kind: 'lumbar', label: 'Accent Pillow', title: 'Make It Yours', help: 'Finish the pillow arrangement with one lumbar or oblong accent.', optional: true, qty: 1 },
  { slot: 'throw', kind: 'throw', label: 'Throw', title: 'A Cozy Finishing Touch', help: 'Drape a throw at the corner for an easy, relaxed finish.', optional: true, qty: 1 },
  { slot: 'sheets', kind: 'sheets', label: 'Sheets', title: 'Choose Your Sheets', help: 'Your sheets peek through the fold at the top of the bed. Skip to keep the white sheets shown in the room.', optional: true, qty: 1 },
];
const PR_BD_EXPECTED_SECONDS = 60;

class PrBedDesigner extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready) return;
    this.dataset.ready = '1';
    this.api = (this.dataset.api || '').replace(/\/$/, '');
    this.brand = this.dataset.brand || 'pr';
    this.brandName = this.dataset.brandName || 'Paseo Road';
    this.room = this.brand === 'pr' ? 'everyday' : 'neutral';
    this.renderedRoom = 'neutral';
    this.shoppingOnly = false;
    this.size = 'queen';
    try {
      if (localStorage.getItem(`${this.brand}-bed-designer-size`) === 'king') this.size = 'king';
    } catch (error) {
      /* storage blocked: default to Queen */
    }
    this.sel = {}; // Begin with the shopper's own choices; saved drafts still restore.
    this.rendered = null;
    this.screen = 0;
    this.visited = 0;
    this.sorts = {};
    this.types = {};
    this.filters = {};
    this.expanded = {};
    this.searches = {};
    this.items = {}; this.inserts=[]; this.insertChoices={}; this.roomReady=false; this.catalogReady=false; this.setupChanged=false;
    this.$ = (s) => this.querySelector(s);
    this.addEventListener('click', (event) => this.onClick(event));
    this.addEventListener('change', (event) => {
      const control = event.target;
      if(control.matches('[data-upload-file]'))this.prepareUpload(control.files?.[0]);
      if(control.matches('[data-insert-check]')){const kind=control.dataset.insertCheck;this.insertChoices[kind]={...this.insertChoices[kind],checked:control.checked};this.applySize();}
      if(control.matches('[data-insert-variant]')){const kind=control.dataset.insertVariant;this.insertChoices[kind]={...this.insertChoices[kind],id:Number(control.value)};this.applySize();}

      if (control.matches('[data-sort]')) { this.sorts[control.dataset.sort] = control.value; this.syncSteps(); }
      if (control.matches('[data-type-filter]')) { this.types[control.dataset.typeFilter] = control.value; this.expanded[control.dataset.typeFilter] = 6; this.syncSteps(); }
      if (control.matches('[data-purchase-format]')) {
        const row = control.closest('[data-piece]');
        if (this.formsFor(row.dataset.piece).some(([key]) => key === control.value)) { row.dataset.purchaseKey = control.value; this.applySize(); this.updateShareLinks(); }
      }
    });
    this.addEventListener('input', (event) => {
      if (!event.target.matches('[data-search]')) return;
      const slot = event.target.dataset.search;
      this.searches[slot] = event.target.value;
      this.expanded[slot] = 6;
      this.syncSteps();
    });
    this.publicShareId=null;this.defaultDisclosure=this.$('[data-image-disclosure]').textContent;
    this.renderStudio();
    this.loadRooms();
    this.load();
  }

  parseCombo(text) {
    const sel = {};
    text.split(';').forEach((pair) => {
      const [slot, key] = pair.split('=').map((s) => (s || '').trim());
      if (slot && key) sel[slot] = key;
    });
    return sel;
  }

  comboKey(sel) {
    return PR_BD_SLOTS.filter(({ slot }) => sel[slot]).map(({ slot }) => `${slot}=${sel[slot]}`).join(';');
  }

  money(cents) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: window.Shopify?.currency?.active || 'USD' }).format(cents / 100);
  }

  async loadRooms() {
    try {
      const response=await fetch(`${this.api}/rooms?v=13`);if(!response.ok)throw new Error('rooms');
      const data=await response.json();if(data.brand&&data.brand!==this.brand)throw new Error('brand');
      this.rooms=[...data.rooms,...(this.rooms||[]).filter(r=>r.private||r.readOnly)];this.roomReady=true;this.renderRooms();this.syncStudio();
    }catch{this.setStatus('[data-status]','The rooms could not load just now. Please refresh to try again.','error');}
  }

  async load() {
    try {
      const r = await fetch(`${this.api}/catalog?v=18`);
      if (!r.ok) throw new Error('catalog');
      const catalog = await r.json();
      if (catalog.brand && catalog.brand !== this.brand) throw new Error('brand');
      this.items = catalog.items || {};
      this.inserts=catalog.inserts||[];
      this.rooms = catalog.rooms || [{id:'neutral',name:'Warm Neutral',caption:'Walnut, linen and natural light.',image:'/rooms/neutral.jpg'}];
      this.brandName = catalog.brandName || this.brandName;
      if (!Object.keys(this.items).length || Object.values(this.items).some((item) => !item.visualKey)) throw new Error('catalog');
      this.formIndex = new Map();
      Object.entries(this.items).forEach((entry) => { const key = entry[1].visualKey; if (!this.formIndex.has(key)) this.formIndex.set(key, []); this.formIndex.get(key).push(entry); });
      this.formIndex.forEach((entries) => entries.sort((a,b) => (a[1].pieceType === 'comforter' ? 0 : 1) - (b[1].pieceType === 'comforter' ? 0 : 1)));
    } catch (error) {
      this.setStatus('[data-status]', 'The designer could not load just now. Please refresh the page.', 'error');
      return;
    }
    for (const slot of Object.keys(this.sel)) if (!this.items[this.sel[slot]]) delete this.sel[slot];
    this.roomReady=true;this.catalogReady=true;this.renderRooms();
    const setup={room:this.room,size:this.size};await this.restorePrivateRoom();this.restoreDraft();
    if(this.setupChanged){this.room=setup.room;this.size=setup.size;this.syncRooms();}
    this.renderSteps();
    this.applySize();
    this.showScreen(this.screen, false);
    await this.restoreSharedLook();
  }

  // Room photos are guest-owned. Secrets stay in this tab's session storage, never in a look URL.
  privateHeaders(path) {
    let target;try{target=new URL(path,this.api);}catch{return {};}if(target.origin!==new URL(this.api).origin)return {};
    const id=target.pathname.match(/^\/private\/([a-f0-9]{32})(?:\/|$)/)?.[1];
    return id && this.ownedRoom?.id===id ? {Authorization:`Bearer ${this.ownedRoom.key}`} : {};
  }
  roomURL(room,thumb=false) { const src=thumb?(room.thumb||room.image):room.image; return src?.startsWith('blob:')?src:`${this.api}${src}`; }
  showVerification(text) { if(this.photoChecking)this.setStatus('[data-upload-status]',text);else this.showOverlay(text,false); }
  async restorePrivateRoom() {
    try {
      const record=JSON.parse(sessionStorage.getItem(`${this.brand}-bd-private-room`)||'null');
      if(!/^[a-f0-9]{32}$/.test(record?.id)||!/^[a-f0-9]{64}$/.test(record?.key)||record.expires<=Date.now())return;
      this.ownedRoom=record;
      const r=await fetch(`${this.api}/private/${record.id}/room`,{headers:this.privateHeaders(`/private/${record.id}`)});
      if(!r.ok){if(r.status===404||r.status===410){this.ownedRoom=null;try{sessionStorage.removeItem(`${this.brand}-bd-private-room`);}catch{}throw Error('expired');}throw Error('temporary');}const data=await r.json();this.$('[data-upload-ready]').hidden=false;this.$('[data-upload-use]').disabled=true;this.$('[data-upload-thumbnail]').hidden=true;await this.installPrivateRoom(data.room,false);
    } catch (error) { this.setStatus('[data-upload-status]',error.roomExpired?'Your original room photo has expired. Saved previews remain available until their expiry; delete this room before uploading a new photo.':this.ownedRoom?'Your room photo could not load just now. Your access is saved in this tab. Refresh to try again; your bedding choices are saved.':'Your room photo is unavailable or expired. You can upload it again; your bedding choices are saved.'); }
  }
  async installPrivateRoom(room,select=true) {
    const response=await fetch(this.roomURL(room),{headers:this.privateHeaders(room.image)});
    if(!response.ok){const error=Error('Your room photo could not load just now. Refresh to try again.');error.roomExpired=response.status===404||response.status===410;throw error;}
    const blob=await response.blob();if(blob.type!=='image/jpeg')throw Error('Photo unavailable');
    if(this.privatePhotoURL)URL.revokeObjectURL(this.privatePhotoURL);
    this.privatePhotoURL=URL.createObjectURL(blob);room={...room,image:this.privatePhotoURL,thumb:this.privatePhotoURL};
    this.rooms=this.rooms.filter(r=>!r.private);this.rooms.push(room);
    this.$('[data-upload-fields]').hidden=true;this.$('[data-upload-layout]').hidden=true;
    this.$('[data-upload-ready]').hidden=false;this.$('[data-upload-thumbnail]').hidden=false;this.$('[data-upload-thumbnail]').src=room.image;this.$('[data-upload-use]').disabled=false;this.$('[data-upload-file]').disabled=true;this.$('[data-upload-consent]').disabled=true;
    this.$('[data-upload-use]').dataset.roomChoice=room.id;
    if(select){this.setupChanged=true;this.room=room.id;this.roomMode='upload';}
    this.syncRooms();
  }
  async prepareUpload(file) {
    const revision=(this.uploadRevision||0)+1;this.uploadRevision=revision;this.preparedPhoto=null;this.pendingUpload=null;this.$('[data-upload-submit]').disabled=true;
    const localPhoto=this.$('[data-local-photo]');if(localPhoto){localPhoto.hidden=true;localPhoto.removeAttribute('src');}
    if(!file){this.setStatus('[data-upload-status]','');return;}
    this.setStatus('[data-upload-status]','Preparing your photo…');
    try {
      if(!['image/jpeg','image/png','image/webp'].includes(file.type)||file.size>10*1024*1024)throw Error('Choose a JPG, PNG or WebP photo under 10 MB. For an iPhone HEIC photo, export or save it as JPG first.');
      const bytes=new Uint8Array(await file.arrayBuffer());const dims=this.uploadDimensions(bytes,file.type);
      if(!dims)throw Error('Choose a still JPG, PNG or WebP photo that your browser can open.');
      if(!Number.isSafeInteger(dims.width)||!Number.isSafeInteger(dims.height)||dims.width<=0||dims.height<=0||dims.width*dims.height>64000000)throw Error('This photo is unusually large. Choose a photo up to 64 megapixels.');
      if(Math.min(dims.width,dims.height)<480||Math.max(dims.width,dims.height)<640)throw Error('This photo is too small. Choose one at least 640 × 480 pixels, or 480 × 640 for a portrait photo.');
      let bitmap;try{bitmap=await createImageBitmap(file);}catch{throw Error('This photo could not open. Try saving it as a JPG or choose another photo.');}
      const ratio=Math.min(1,1600/Math.max(bitmap.width,bitmap.height));const canvas=document.createElement('canvas');canvas.width=Math.round(bitmap.width*ratio);canvas.height=Math.round(bitmap.height*ratio);
      if(canvas.width/canvas.height<.65||canvas.width/canvas.height>1.9){bitmap.close();throw Error('Choose a landscape or portrait room photo with a little more space around the bed. Very narrow photos and panoramas cannot be used yet.');}
      const context=canvas.getContext('2d',{colorSpace:'srgb'});context.fillStyle='#fff';context.fillRect(0,0,canvas.width,canvas.height);context.drawImage(bitmap,0,0,canvas.width,canvas.height);bitmap.close();
      let blob;for(const quality of [.84,.74,.64]){blob=await new Promise(resolve=>canvas.toBlob(resolve,'image/jpeg',quality));if(blob&&blob.size<=1000000)break;}
      if(!blob||blob.size>1000000)throw Error('This photo is too detailed to upload safely. Try a smaller JPG.');
      blob=new Blob([this.stripPhotoMetadata(new Uint8Array(await blob.arrayBuffer()))],{type:'image/jpeg'});
      const image=await new Promise((resolve,reject)=>{const reader=new FileReader();reader.onload=()=>resolve(reader.result);reader.onerror=reject;reader.readAsDataURL(blob);});
      if(revision!==this.uploadRevision)return;
      this.preparedPhoto=image;this.$('[data-upload-submit]').disabled=false;
      if(localPhoto){localPhoto.src=image;localPhoto.hidden=false;}
      this.setStatus('[data-upload-status]','Photo ready. We have resized it automatically and removed embedded location data.');
    }catch(error){if(revision===this.uploadRevision)this.setStatus('[data-upload-status]',error.message||'This photo could not open. Please choose another.','error');}
  }
  stripPhotoMetadata(bytes) {
    // Canvas pixels are already converted to sRGB. Remove encoder profiles and all optional private metadata;
    // retain the compressed pixel stream unchanged, so this adds no extra JPEG generation or image drift.
    if(bytes[0]!==255||bytes[1]!==216)throw Error('This photo could not be prepared safely. Choose another JPG or PNG.');
    const parts=[bytes.subarray(0,2)];let pos=2,scan=false;
    while(pos+4<bytes.length){const start=pos;if(bytes[pos++]!==255)throw Error('Invalid photo');while(bytes[pos]===255)pos++;const marker=bytes[pos++],length=bytes[pos]*256+bytes[pos+1];if(length<2||pos+length>bytes.length)throw Error('Invalid photo');
      if(marker===218){parts.push(bytes.subarray(start));scan=true;break;}
      if(!((marker>=225&&marker<=239)||marker===254||(marker===224&&length>16)))parts.push(bytes.subarray(start,pos+length));pos+=length;
    }
    if(!scan)throw Error('Invalid photo');const clean=new Uint8Array(parts.reduce((n,p)=>n+p.length,0));let offset=0;parts.forEach(p=>{clean.set(p,offset);offset+=p.length;});return clean;
  }
  uploadDimensions(b,type) {
    const u16=(p)=>b[p]*256+b[p+1],u32=p=>(b[p]*16777216+b[p+1]*65536+b[p+2]*256+b[p+3]);
    if(type==='image/png'&&b[0]===137&&b[1]===80&&b[2]===78&&b[3]===71&&b.length>=24)return {width:u32(16),height:u32(20)};
    if(type==='image/jpeg'&&b[0]===255&&b[1]===216){let p=2;while(p+4<b.length){if(b[p++]!==255)return null;while(b[p]===255)p++;const marker=b[p++],len=u16(p);if(len<2||p+len>b.length)return null;if([192,193,194].includes(marker)){if(len<8)return null;return {height:u16(p+3),width:u16(p+5)};};if(marker===218)return null;p+=len;}}
    if(type==='image/webp'&&String.fromCharCode(...b.slice(0,4))==='RIFF'&&String.fromCharCode(...b.slice(8,12))==='WEBP'){
      const kind=String.fromCharCode(...b.slice(12,16));if(kind==='VP8X'&&b.length>=30&&!(b[20]&2))return {width:1+b[24]+b[25]*256+b[26]*65536,height:1+b[27]+b[28]*256+b[29]*65536};
      if(kind==='VP8L'&&b[20]===47&&b.length>=25){const bits=(b[21]|b[22]<<8|b[23]<<16|b[24]<<24)>>>0;return {width:1+(bits&16383),height:1+((bits>>>14)&16383)};}
      if(kind==='VP8 '&&b[23]===157&&b[24]===1&&b[25]===42)return {width:(b[26]|b[27]<<8)&16383,height:(b[28]|b[29]<<8)&16383};
    }return null;
  }
  async uploadOwnRoom() {
    if(this.photoChecking)return;
    if(this.ownedRoom){this.setStatus('[data-upload-status]','Delete your previous room photo before replacing it. Your bedding choices will stay saved.','error');return;}
    if(!this.preparedPhoto){this.setStatus('[data-upload-status]','Choose a room photo first.','error');return;}
    if(!this.$('[data-upload-consent]').checked){this.setStatus('[data-upload-status]','Please agree to AI processing before we check your photo.','error');return;}
    const hex=n=>[...crypto.getRandomValues(new Uint8Array(n))].map(b=>b.toString(16).padStart(2,'0')).join('');
    const pending=this.pendingUpload||{id:hex(16),key:hex(32)};this.pendingUpload=pending;
    this.photoChecking=true;this.syncSteps();this.$('[data-upload-file]').disabled=true;this.$('[data-upload-submit]').disabled=true;
    this.verificationSlot=this.$('[data-upload-verification]');this.setStatus('[data-upload-status]','Connecting the quick security check…');
    try {
      const token=await this.getToken();this.setStatus('[data-upload-status]','Checking that your bed is visible and your photo is suitable…');
      const r=await fetch(`${this.api}/uploads`,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({id:pending.id,ownerKey:pending.key,image:this.preparedPhoto,size:this.size,consent:true,token}),signal:AbortSignal.timeout(90000)});
      const data=await r.json();if(!r.ok||data.status!=='ready')throw Error(data.message||'Your photo could not be checked just now. Please try again.');
      if(this.ownedRoom&&this.ownedRoom.id!==pending.id)throw Error('Delete your previous room photo before replacing it.');
      this.ownedRoom={...pending,expires:data.room.expires};try{sessionStorage.setItem(`${this.brand}-bd-private-room`,JSON.stringify(this.ownedRoom));}catch{/* Current page still works when storage is blocked. */}
      await this.installPrivateRoom(data.room);this.preparedPhoto=null;this.pendingUpload=null;this.$('[data-upload-file]').value='';this.$('[data-upload-consent]').checked=false;
      this.setStatus('[data-upload-status]',['Your photo is ready. Choose Your Bedding to start styling in your room.',...(data.warnings||[])].join(' '));this.saveDraft();
    } catch(error) {this.setStatus('[data-upload-status]',error.message||'We could not check your photo just now. Try again, or select Our Rooms to keep styling.','error');}
    finally {this.photoChecking=false;this.verificationSlot=null;this.$('[data-upload-file]').disabled=!!this.ownedRoom;this.$('[data-upload-consent]').disabled=!!this.ownedRoom;this.$('[data-upload-submit]').disabled=!this.preparedPhoto||!!this.ownedRoom;this.syncSteps();}
  }
  async createPrivateShare() {
    if(!this.ownedRoom||!this.renderImage)return;
    if(!this.$('[data-share-consent]').checked){this.setStatus('[data-share-status]','Confirm that anyone with the link may see this room preview.','error');return;}
    const button=this.$('[data-create-share]');button.disabled=true;
    try {
      const path=`/private/${this.ownedRoom.id}/share`,hash=this.renderImage.match(/([a-f0-9]{64})\.jpg$/)?.[1];
      const r=await fetch(this.api+path,{method:'POST',headers:{'Content-Type':'application/json',...this.privateHeaders(path)},body:JSON.stringify({hash,consent:true})});const data=await r.json();
      if(!r.ok||!/^[a-f0-9]{32}$/.test(data.shareId))throw Error(data.error||'The share link could not be created just now.');
      this.publicShareId=data.shareId;this.updateShareLinks();this.setStatus('[data-share-status]','Share link ready. Copy or email it below. It expires on '+new Date(data.expires).toLocaleDateString(undefined,{month:'short',day:'numeric'})+'.');
    }catch(error){this.setStatus('[data-share-status]',error.message,'error');}finally{button.disabled=false;}
  }
  async deletePrivateRoom() {
    if(!this.ownedRoom||!window.confirm('Delete your room photo, its previews, and any shared links? Your bedding selections will stay saved.'))return;
    const path=`/private/${this.ownedRoom.id}`;const button=this.$('[data-delete-room]');button.disabled=true;
    try{
      const r=await fetch(this.api+path,{method:'DELETE',headers:this.privateHeaders(path)});if(!r.ok)throw Error('Your photo could not be deleted just now. Please try again.');
      const id=this.ownedRoom.id;this.ownedRoom=null;try{sessionStorage.removeItem(`${this.brand}-bd-private-room`);}catch{}this.rooms=this.rooms.filter(r=>r.id!==`upload-${id}`);
      if(this.privatePhotoURL)URL.revokeObjectURL(this.privatePhotoURL);this.privatePhotoURL=null;this.preparedPhoto=null;this.pendingUpload=null;this.$('[data-upload-file]').value='';this.$('[data-upload-fields]').hidden=false;this.$('[data-upload-layout]').hidden=false;this.$('[data-upload-submit]').disabled=true;this.$('[data-upload-ready]').hidden=true;this.$('[data-upload-thumbnail]').src='';const localPhoto=this.$('[data-local-photo]');if(localPhoto){localPhoto.hidden=true;localPhoto.removeAttribute('src');}this.$('[data-upload-file]').disabled=false;this.$('[data-upload-consent]').disabled=false;
      if(this.renderedRoom===`upload-${id}`&&this.room!==`upload-${id}`){this.rendered=null;this.renderImage=null;this.publicShareId=null;if(this.renderBlob)URL.revokeObjectURL(this.renderBlob);this.renderBlob=null;this.$('[data-stage-image]').hidden=true;this.$('[data-stage-image]').src='';}
      if(this.room===`upload-${id}`){this.room=this.brand==='pr'?'everyday':'neutral';this.roomMode='upload';this.rendered=null;this.renderImage=null;this.publicShareId=null;if(this.renderBlob)URL.revokeObjectURL(this.renderBlob);this.renderBlob=null;this.$('[data-stage-image]').hidden=true;this.$('[data-stage-image]').src='';this.syncRooms();this.showScreen(1);}
      this.setStatus('[data-upload-status]','Your room photo, previews and shared links were removed. Your bedding choices are saved.');this.saveDraft();
    }catch(error){this.setStatus('[data-upload-status]',error.message,'error');}finally{button.disabled=false;}
  }

  renderStudio() {
    const journey = this.$('[data-journey]'); journey.replaceChildren();
    ['Size', 'Room', ...PR_BD_SLOTS.map(step => step.label), 'Review'].forEach((label,index) => {
      const button=document.createElement('button');button.type='button';button.className='pr-bed-designer__journey-step';button.dataset.journey=index;button.textContent=label;journey.append(button);
    });
    const tray=this.$('[data-look-tray]');tray.replaceChildren();
    PR_BD_SLOTS.forEach((step,index) => {
      const button=document.createElement('button');button.type='button';button.className='pr-bed-designer__tray-piece';button.dataset.goto=index+2;button.dataset.traySlot=step.slot;
      const image=document.createElement('img');image.alt='';image.width=128;image.height=128;
      const plus=document.createElement('span');plus.className='pr-bed-designer__tray-empty';plus.textContent='+';plus.setAttribute('aria-hidden','true');
      const label=document.createElement('span');label.className='pr-bed-designer__tray-label';label.textContent=step.label;button.append(image,plus,label);tray.append(button);
    });
  }

  syncStudio() {
    const busy = this.dataset.state === 'rendering' || this.photoChecking || this.resetting || this.restoringLook || this.adding;
    this.$('[data-begin]').disabled = busy || !this.roomReady;
    this.$('[data-edit-bed]').disabled=busy;
    this.$('[data-chapter]').textContent = String(Math.min(9, this.screen + 1)).padStart(2, '0');
    this.$('[data-studio]').hidden = this.screen < 2;
    this.$('[data-tray]').hidden = this.screen < 2;
    this.querySelectorAll('[data-journey]').forEach((el) => { el.hidden = this.screen === 0; });
    this.querySelectorAll('[data-journey] button').forEach((button) => {
      const index = Number(button.dataset.journey);
      button.disabled = busy || index > this.visited;
      if (index === this.screen) button.setAttribute('aria-current', 'step'); else button.removeAttribute('aria-current');
      button.dataset.complete = String(index < this.screen);
    });
    this.querySelectorAll('[data-tray-slot]').forEach((button) => {
      const slot = button.dataset.traySlot;
      const key = this.sel[slot]; const item = this.items[key]; const img = button.querySelector('img');
      img.hidden = !item; button.querySelector('.pr-bed-designer__tray-empty').hidden = !!item;
      if (item && img.getAttribute('src') !== item.thumb) img.src = item.thumb;
      button.dataset.filled = String(!!item);
      button.setAttribute('aria-label', item ? `Edit ${PR_BD_SLOTS.find((step) => step.slot === slot).label}: ${item.name}${item.color ? ', ' + item.color : ''}` : `Choose ${PR_BD_SLOTS.find((step) => step.slot === slot).label}`);
      if (PR_BD_SLOTS[this.screen - 2]?.slot === slot) button.setAttribute('aria-current', 'step'); else button.removeAttribute('aria-current');
      button.disabled = busy;
    });
    const count = Object.values(this.sel).filter(Boolean).length;
    this.$('[data-tray-count]').textContent = `${this.size === 'king' ? 'King' : 'Queen'} · ${count} ${count === 1 ? 'layer' : 'layers'}`;
    this.$('[data-navigation-choice]').textContent = this.screen === 8 ? 'A look that’s all your own.' : this.items[this.sel[PR_BD_SLOTS[this.screen - 2]?.slot]]?.name || 'Make room for something you love.';
  }

  formsFor(key) {
    const item = this.items[key];
    if (!item) return [];
    return this.formIndex?.get(item.visualKey) || [];
  }

  sameVisual(a,b) { return !!a && !!b && this.items[a]?.visualKey === this.items[b]?.visualKey; }

  displayAvailable(key) { return this.formsFor(key).some(([candidate]) => this.available(candidate)); }

  bedSized(key) {
    const item=this.items[key];if(!item)return false;
    if(item.queen?.id&&item.king?.id&&String(item.queen.id)===String(item.king.id))return false;
    return ['comforter','duvet','quilt','coverlet','bedspread','blanket','sheets'].includes(item.pieceType);
  }

  unavailableLabel(key) {
    const variant=this.variantFor(key),size=this.size==='king'?'King':'Queen';
    return variant?(this.bedSized(key)?`Sold out in ${size}`:'Sold out'):(this.bedSized(key)?`Not made in ${size}`:'Currently unavailable');
  }

  stockNote(key) {
    if (this.displayAvailable(key)) return '';
    const forms=this.formsFor(key),candidate=forms.find(([,item])=>item[this.size])?.[0]||key;
    return this.unavailableLabel(candidate).replace(/^Sold out/,'Sold Out').replace(/^Not made/,'Not Made')+' · Preview Only';
  }

  savedLookURL() {
    if (!this.renderImage || this.shoppingOnly) return '';
    const hash = this.renderImage.match(/([a-f0-9]{64})\.jpg$/)?.[1]; if (!hash&&!this.publicShareId) return '';
    const current = new URL(window.location.href);
    const url = new URL(current.pathname, current.origin);
    if (current.searchParams.has('view')) url.searchParams.set('view', current.searchParams.get('view'));
    if (this.dataset.themeId && this.dataset.themeRole !== 'main') { url.searchParams.set('preview_theme_id', this.dataset.themeId); url.searchParams.set('pb', '0'); }
    const params = this.publicShareId?new URLSearchParams({shared:this.publicShareId,size:this.size}):new URLSearchParams({look:hash,size:this.size});
    if(this.renderedRoom?.startsWith('upload-')&&!this.publicShareId)params.set('private_room',this.renderedRoom.slice(7));
    this.querySelectorAll('[data-piece]').forEach((row) => { if (row.dataset.purchaseKey) params.set(`form_${row.dataset.slot}`, row.dataset.purchaseKey); });
    url.hash = params.toString(); return url.href;
  }

  syncShareActions(busy=false) {
    const privateUnshared=this.renderedRoom?.startsWith('upload-')&&!this.publicShareId;
    this.$('.pr-bed-designer__share').hidden=busy||!this.renderImage||this.shoppingOnly;
    this.querySelectorAll('[data-share-action]').forEach(control=>{if(control.tagName==='BUTTON')control.disabled=busy;control.hidden=busy||this.shoppingOnly;});
    this.$('[data-copy-look]').hidden=busy||this.shoppingOnly||!!privateUnshared;
    this.$('[data-email-look]').hidden=busy||this.shoppingOnly||!!privateUnshared;
  }
  updateShareLinks() {
    const privateLook=this.renderedRoom?.startsWith('upload-');
    const url=this.savedLookURL(),image=this.publicShareId?`/shared/${this.publicShareId}.jpg`:this.renderImage||'';
    this.$('[data-copy-look]').hidden=!!privateLook&&!this.publicShareId;
    this.$('[data-email-look]').hidden=!!privateLook&&!this.publicShareId;
    this.$('[data-private-sharing]').hidden=!privateLook;
    this.$('[data-share-disclosure]').textContent=privateLook&&!this.publicShareId?'Your room preview stays private. Create a share link only if you want others to see your room.':privateLook?'Anyone with your share link can view this room preview. Delete your room photo to revoke its links.':'Email opens a prepared message in your email app. Saved links reopen the image and its pieces.';
    this.$('[data-email-look]').href=`mailto:?subject=${encodeURIComponent(`My ${this.brandName} Bed`)}&body=${encodeURIComponent(`Here is my bedding look.\n\nOpen the look and shop the pieces:\n${url}\n\nView the image:\n${this.api}${image}`)}`;
    this.$('[data-share-link]').value=url;
    this.syncShareActions(this.dataset.state==='rendering');
    const disclosure=this.$('[data-image-disclosure]');if(disclosure)disclosure.textContent=(privateLook||this.renderedRoom?.startsWith('shared-'))?'Styled in the uploaded room. This image was created with AI from your photo and our product photos. Colors, patterns, stitching and scale may differ; see each product page for exact details.':this.defaultDisclosure;
  }

  async saveImage() {
    if (!this.renderImage) return;
    const button = this.$('[data-save-image]'); button.disabled = true;
    this.setStatus('[data-share-status]', 'Preparing your image…');
    try {
      const response = await fetch(`${this.api}${this.renderImage}`,{headers:this.privateHeaders(this.renderImage)});
      if (!response.ok) throw new Error('image');
      const blob = await response.blob();
      if (!blob.type.startsWith('image/')) throw new Error('image');
      const url = URL.createObjectURL(blob);
      const download = document.createElement('a'); download.href = url; download.download = `${this.brandName.replaceAll(' ', '-')}-My-Bed.jpg`;
      document.body.append(download); download.click(); download.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      this.setStatus('[data-share-status]', 'Your image download is ready.');
    } catch { this.setStatus('[data-share-status]', 'The image could not download just now. You can still copy or email your look link.', 'error'); }
    finally { button.disabled = false; }
  }

  async copyLook() {
    this.updateShareLinks();
    try {
      if (!navigator.clipboard?.writeText) throw new Error('clipboard');
      await navigator.clipboard.writeText(this.savedLookURL());
      this.setStatus('[data-share-status]', 'Look link copied.');
    } catch {
      this.$('[data-share-link]').hidden = false; this.$('[data-share-link]').select();
      this.setStatus('[data-share-status]', 'Copy the link below.');
    }
  }

  async restoreSharedLook() {
    const params = new URLSearchParams(window.location.hash.slice(1));
    const hash=params.get('look'),share=params.get('shared'),privateId=params.get('private_room');
    if(!(share&&/^[a-f0-9]{32}$/.test(share))&&(!hash||!/^[a-f0-9]{64}$/.test(hash)))return;
    this.restoringLook=true;this.syncSteps();
    try {
      const path=share?`/shared/${share}`:privateId?`/private/${privateId}/looks/${hash}`:`/looks/${hash}`;
      const response=await fetch(this.api+path,{headers:this.privateHeaders(path)});
      if (!response.ok) throw new Error('This saved look could not be found. You can start a new design below.');
      const look = await response.json();
      if (!look.slots?.base || Object.values(look.slots).some((key) => !this.items[key]) || !/^\/(?:r\/[a-f0-9]{64}|private\/[a-f0-9]{32}\/r\/[a-f0-9]{64}|shared\/[a-f0-9]{32})\.jpg$/.test(look.image)) throw new Error('Some pieces in this saved look are no longer in the designer.');
      this.publicShareId=share||null;
      if(look.roomInfo&&!this.rooms.some(r=>r.id===look.roomInfo.id)){const info={...look.roomInfo};if(info.private){const r=await fetch(this.api+look.image,{headers:this.privateHeaders(look.image)});if(!r.ok)throw Error('Preview unavailable');info.image=URL.createObjectURL(await r.blob());info.thumb=info.image;}this.rooms.push(info);}
      this.size = params.get('size') === 'king' ? 'king' : 'queen';
      this.sel = { ...look.slots }; this.rendered = { ...look.slots }; this.renderImage = look.image; this.renderRecipe = look.recipe; this.room = look.room || 'neutral'; this.renderedRoom = this.room; this.syncRooms();
      this.renderQuality=look.quality; this.renderWarnings=look.warnings; this.setQualityNote(look.quality, look.warnings); this.$('[data-result-title]').textContent=look.quality==='approximate'?'Your Styling Preview':'Your Bed Is Ready';
      this.renderSteps(); await this.showImage(`${this.api}${look.image}`); this.dataset.state = 'ready'; this.buildCart();
      this.querySelectorAll('[data-piece]').forEach((row) => {
        const key = params.get(`form_${row.dataset.slot}`);
        if (key && this.formsFor(row.dataset.piece).some(([candidate]) => candidate === key)) { row.dataset.purchaseKey = key; const select = row.querySelector('[data-purchase-format]'); if (select) select.value = key; }
      });
      this.applySize(); this.updateShareLinks(); this.$('[data-result-title]').textContent = look.quality==='approximate' ? 'Your Saved Styling Preview' : 'Your Saved Look'; this.showScreen(9);
    } catch (error) { this.setStatus('[data-status]', error.message, 'error'); }
    finally{this.restoringLook=false;this.syncSteps();}
  }

  // Room choice and local draft are free; only the final design action may render.
  renderRooms() {
    const list = this.$('[data-room-options]'); list.replaceChildren();
    this.rooms.filter(room=>!room.private&&!room.readOnly).forEach(room => {
      const button = document.createElement('button'); button.type = 'button'; button.className = 'pr-bed-designer__room'; button.dataset.roomChoice = room.id;
      const img = document.createElement('img'); img.src = this.roomURL(room,true); img.alt = ''; img.width = 400; img.height = 300; img.loading = 'eager';img.decoding='async';
      const name = document.createElement('span'); name.className = 'pr-bed-designer__option-name'; name.textContent = room.name;
      const caption = document.createElement('span'); caption.className = 'pr-bed-designer__studio-note'; caption.textContent = room.caption;
      button.append(img,name,caption); list.append(button);
    });
    this.syncRooms();
  }

  setRoomMode(mode) {
    if(this.photoChecking)return;const current=this.rooms?.find(r=>r.id===this.room);if(current&&!current.private&&!current.readOnly)this.lastPresetRoom=current.id;
    this.roomMode=mode==='upload'?'upload':'preset';
    if(this.roomMode==='preset'&&(current?.private||current?.readOnly))this.room=this.lastPresetRoom||(this.brand==='pr'?'everyday':'neutral');
    if(this.roomMode==='upload'&&this.ownedRoom)this.room=`upload-${this.ownedRoom.id}`;
    this.syncRooms();this.syncSteps();
  }

  syncRoomMode(room) {
    const mode=this.roomMode||(room?.private?'upload':'preset');
    this.querySelectorAll('[data-room-mode]').forEach(button=>{button.setAttribute('aria-pressed',String(button.dataset.roomMode===mode));button.disabled=!!this.photoChecking;});
    const preset=this.$('[data-preset-panel]'),upload=this.$('[data-upload-panel]'),shared=this.$('[data-shared-room-note]');
    if(preset)preset.hidden=mode!=='preset';if(upload)upload.hidden=mode!=='upload';if(shared)shared.hidden=!room?.readOnly;
  }

  syncRooms() {
    if (!this.rooms) return;
    if (!this.rooms.some(room => room.id === this.room)) this.room = 'neutral';
    this.querySelectorAll('[data-room-choice]').forEach(button => { button.setAttribute('aria-pressed',String(button.dataset.roomChoice===this.room)); button.disabled=this.dataset.state==='rendering'||this.photoChecking; });
    const room = this.rooms.find(room => room.id===this.room);
    this.syncRoomMode(room);
    const selected=this.$('[data-upload-use]');if(selected)selected.textContent=room.private?'Your Room ✓':'Use Your Room';
    const img=this.$('[data-room-thumb]'); if (img) { img.src=this.roomURL(room); img.alt=''; }
    this.$('[data-room-name]').textContent=room.name;
    const preview=this.$('[data-room-preview]'),inspiration=this.$('[data-inspiration-image]');
    preview.src=this.roomURL(room,true);inspiration.src=preview.src;
    preview.alt=`${room.name} bedroom`;inspiration.alt=`${room.name}, ready for your bedding`;
    const full=new Image();full.src=this.roomURL(room);
    full.decode().then(()=>{if(this.room===room.id){preview.src=full.src;inspiration.src=full.src;}}).catch(()=>{});
    this.$('[data-room-preview-name]').textContent=room.name;
    this.$('[data-room-preview-caption]').textContent=room.caption;
  }

  saveDraft() {
    if (!this.rooms || !Object.keys(this.items).length) return;
    try { if(!Object.keys(this.sel).length&&this.size==='queen'&&this.room===(this.brand==='pr'?'everyday':'neutral')){localStorage.removeItem(`${this.brand}-bed-designer-draft`);return;}localStorage.setItem(`${this.brand}-bed-designer-draft`,JSON.stringify({version:1,slots:this.sel,size:this.size,room:this.room,saved:Date.now()})); } catch {}
  }

  restoreDraft() {
    if (window.location.hash.includes('look=')||window.location.hash.includes('shared=')) return;
    try {
      const draft=JSON.parse(localStorage.getItem(`${this.brand}-bed-designer-draft`) || 'null');
      if (draft?.version!==1 || Date.now()-draft.saved>30*86400000) return;
      this.sel=Object.fromEntries(Object.entries(draft.slots||{}).filter(([slot,key])=>PR_BD_SLOTS.some(step=>step.slot===slot)&&this.items[key]));
      this.size=draft.size==='king'?'king':'queen'; this.room=draft.room||'neutral'; this.syncRooms();
      this.setStatus('[data-status]','Your saved choices are ready to keep styling.');
    } catch {}
  }

  confirmReset() {
    if(this.resetConfirmation)return this.resetConfirmation;
    const dialog=this.$('[data-reset-dialog]'),opener=document.activeElement;
    this.$('[data-reset-message]').textContent=this.ownedRoom?'This clears your design and permanently deletes your uploaded room photo, its previews and shared links.':'This clears your bedding choices, bed size, room and preview.';
    this.resetConfirmation=new Promise(resolve=>{
      dialog.returnValue='';
      dialog.addEventListener('close',()=>{const confirmed=dialog.returnValue==='confirm';this.resetConfirmation=null;if(!confirmed)opener?.focus({preventScroll:true});resolve(confirmed);},{once:true});
      dialog.showModal();this.$('[data-keep-design]').focus({preventScroll:true});
    });
    return this.resetConfirmation;
  }

  async startOver() {
    if(this.resetting||this.resetConfirmation||this.restoringLook||this.photoChecking||this.adding||this.dataset.state==='rendering'||!this.catalogReady)return;
    if(!await this.confirmReset())return;
    this.resetting=true;this.syncSteps();
    try{
      if(this.ownedRoom){const owner=this.ownedRoom,path=`/private/${owner.id}`;const r=await fetch(this.api+path,{method:'DELETE',headers:this.privateHeaders(path),signal:AbortSignal.timeout(20000)});if(!r.ok&&!(r.status===404&&owner.expires&&owner.expires<=Date.now()))throw Error('We could not delete your room photo just now. Your design is still here; please try again.');}
      this.uploadRevision=(this.uploadRevision||0)+1;this.ownedRoom=null;this.pendingUpload=null;this.preparedPhoto=null;
      for(const name of ['privatePhotoURL','renderBlob']){if(this[name])URL.revokeObjectURL(this[name]);this[name]=null;}
      this.rooms=this.rooms.filter(r=>!r.private&&!r.readOnly);this.room=this.brand==='pr'?'everyday':'neutral';this.renderedRoom=this.room;this.roomMode='preset';this.lastPresetRoom=null;
      this.pendingRetry=null;this.retryUntil=0;this.sel={};this.rendered=null;this.renderImage=null;this.renderRecipe=null;this.renderQuality=null;this.renderWarnings=[];this.publicShareId=null;this.shoppingOnly=false;this.shoppingSlots={};
      this.filters={};this.searches={};this.sorts={};this.types={};this.expanded={};this.insertChoices={};this.insertPlanKey=null;this.paletteKey=null;this.paletteCard=null;this.paletteKeys=[];
      this.size='queen';this.screen=0;this.visited=0;this.setupChanged=true;this.dataset.state='ready';
      try{sessionStorage.removeItem(`${this.brand}-bd-private-room`);}catch{}
      try{localStorage.removeItem(`${this.brand}-bed-designer-draft`);localStorage.removeItem(`${this.brand}-bed-designer-size`);}catch{}
      const url=new URL(window.location.href);url.hash='';window.history.replaceState(null,'',url.pathname+url.search);
      const dialog=this.$('[data-color-dialog]');if(dialog?.open)dialog.close();
      for(const selector of ['[data-upload-file]','[data-share-link]'])this.$(selector).value='';
      for(const selector of ['[data-upload-consent]','[data-share-consent]']){this.$(selector).checked=false;this.$(selector).disabled=false;}
      this.$('[data-upload-file]').disabled=false;this.$('[data-upload-submit]').disabled=true;this.$('[data-upload-ready]').hidden=true;this.$('[data-upload-fields]').hidden=false;this.$('[data-upload-layout]').hidden=false;
      for(const selector of ['[data-upload-thumbnail]','[data-local-photo]','[data-stage-image]']){const img=this.$(selector);img.hidden=true;img.removeAttribute('src');}
      this.$('[data-result]').dataset.shoppingOnly='false';this.$('.pr-bed-designer__stage').hidden=false;this.$('[data-private-sharing]').hidden=true;
      for(const selector of ['[data-review-list]','[data-pieces]','[data-insert-options]'])this.$(selector).replaceChildren();this.$('[data-inserts]').hidden=true;this.$('[data-cart]').hidden=true;this.$('[data-add-bed]').disabled=true;
      this.$('[data-copy-look]').disabled=true;this.$('[data-save-image]').href='#';this.$('[data-email-look]').href='#';
      for(const selector of ['[data-status]','[data-cart-status]','[data-upload-status]','[data-share-status]','[data-retry-status]'])this.setStatus(selector,'');this.setQualityNote();
      this.hideOverlay();this.renderSteps();this.applySize();this.syncRooms();this.showScreen(0);this.$('[data-result-title]').textContent='Your Bed Is Ready';
      this.setStatus('[data-status]','Your design has been cleared. Choose your bed size to begin again.');
    }catch(error){const message=error.message||'We could not start over just now. Please try again.';this.setStatus('[data-status]',message,'error');if(this.screen===9)this.setStatus('[data-share-status]',message,'error');}
    finally{this.resetting=false;this.syncSteps();}
  }

  choose(slot,key) {
    if (!this.items[key]) return;
    this.sel[slot]=key;
    const card=[...this.querySelectorAll(`[data-step="${slot}"] [data-family]`)].find(card=>card.querySelector(`[data-color="${key}"], [data-key="${key}"]`));
    if (card) this.fillOption(card.querySelector('.pr-bed-designer__option'),key,this.items[key]);
    this.setStatus('[data-status]',''); this.syncSteps();
  }

  openPalette(card) {
    this.paletteCard=card; this.paletteOpener=card.querySelector('[data-open-palette]');
    this.paletteSlot=card.closest('[data-step]').dataset.step;
    this.paletteKeys=[...card.querySelectorAll('[data-color]')].map(button=>button.dataset.color);
    this.paletteKey=card.querySelector('[data-key]').dataset.key;
    const list=this.$('[data-dialog-colors]'); list.replaceChildren();
    this.paletteKeys.forEach(key=>{
      const item=this.items[key], button=document.createElement('button'); button.type='button'; button.className='pr-bed-designer__color'; button.dataset.dialogColor=key;
      const img=document.createElement('img'); img.src=item.thumb; img.alt=''; img.width=48; img.height=48;
      const name=document.createElement('span'); name.textContent=item.color||item.detail;
      button.append(img,name); list.append(button);
    });
    this.syncPalette(); const dialog=this.$('[data-color-dialog]');
    dialog.onclose=()=>this.paletteOpener?.focus({preventScroll:true}); dialog.showModal();
  }

  syncPalette() {
    const item=this.items[this.paletteKey];
    this.$('[data-dialog-image]').src=item.thumb;
    this.$('[data-dialog-title]').textContent=item.name;
    const prices=this.formsFor(this.paletteKey).map(([,form])=>form[this.size]?.price).filter(price=>Number.isFinite(price));
    const price=prices.length?`${this.formsFor(this.paletteKey).length>1?'From ':''}${this.money(Math.min(...prices))}`:'';
    this.$('[data-dialog-choice]').textContent=[item.color||item.detail,price,this.stockNote(this.paletteKey)].filter(Boolean).join(' · ');
    this.querySelectorAll('[data-dialog-color]').forEach(button=>{
      button.setAttribute('aria-pressed',String(button.dataset.dialogColor===this.paletteKey));
      button.disabled=this.sameVisual(this.paletteSlot==='base'?this.sel.top:this.paletteSlot==='top'?this.sel.base:null,button.dataset.dialogColor);
    });
    this.$('[data-confirm-color]').disabled=this.sameVisual(this.paletteSlot==='base'?this.sel.top:this.paletteSlot==='top'?this.sel.base:null,this.paletteKey);
  }

  // ---------- steps ----------
  renderSteps() {
    const steps = this.$('[data-steps]');
    steps.innerHTML = '';
    PR_BD_SLOTS.forEach(({ slot, kind, label, optional }) => {
      const li = document.createElement('li');
      li.className = 'pr-bed-designer__step';
      li.dataset.step = slot;
      const families = new Map();
      Object.entries(this.items)
        .filter(([, item]) => item.slot === kind && item.browse !== false && (slot !== 'top' || item.topEligible === true))
        .sort((a, b) => (a[1].rank || 1000) - (b[1].rank || 1000))
        .forEach(([key, item]) => {
          const family = item.visualFamily || item.family || item.handle;
          if (!families.has(family)) families.set(family, []);
          families.get(family).push([key, item]);
        });
      // Deduplicate appearance, preserving the selected concrete purchase SKU when present.
      families.forEach((entries, family) => {
        const colors = new Map();
        entries.sort((a,b) => Number(b[0] === this.sel[slot]) - Number(a[0] === this.sel[slot]) || (a[1].pieceType === 'comforter' ? 0 : 1) - (b[1].pieceType === 'comforter' ? 0 : 1));
        entries.forEach((entry) => { if (!colors.has(entry[1].color || '')) colors.set(entry[1].color || '', entry); });
        families.set(family, [...colors.values()]);
      });
      const groups = kind === 'cover' ? ['Solids & Textures', 'Patterns'] : [...new Set([...families.values()].map((choices) => choices[0][1].group).filter(Boolean))];
      const filters = document.createElement('div');
      filters.className = 'pr-bed-designer__filters';
      filters.setAttribute('role', 'group');
      filters.setAttribute('aria-label', `${label} designs`);
      ['All Designs', ...groups].forEach((group) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'pr-bed-designer__filter';
        button.dataset.filter = group;
        button.dataset.slot = slot;
        button.textContent = group;
        filters.append(button);
      });
      const choice = document.createElement('p');
      choice.className = 'pr-bed-designer__step-choice';
      choice.dataset.choice = '';
      const list = document.createElement('ul');
      list.className = 'pr-bed-designer__options';
      list.setAttribute('aria-label', label);
      [...families.entries()].sort((a, b) => (slot === 'top' ? Number(['comforter','duvet'].includes(a[1][0][1].pieceType)) - Number(['comforter','duvet'].includes(b[1][0][1].pieceType)) : 0)).forEach(([family, choices]) => {
        const card = document.createElement('li');
        card.className = 'pr-bed-designer__family';
        card.dataset.family = family;
        card.dataset.order = list.children.length;
        card.dataset.group = kind === 'cover' ? choices[0][1].designGroup : choices[0][1].group || '';
        card.dataset.typeGroup = choices[0][1].typeGroup || '';
        card.dataset.searchText = choices.map(([key, item]) => `${item.name} ${item.detail} ${item.color} ${item.group} ${item.typeGroup || ''} ${this.formsFor(key).map(([, form]) => form.detail).join(' ')}`).join(' ').toLowerCase();
        const chosen = choices.find(([key]) => key === this.sel[slot]) || choices.find(([key]) => this.displayAvailable(key)) || choices[0];
        card.append(this.optionButton(slot, chosen[0], chosen[1]));
        if (choices.length > 1) {
          const palette = document.createElement('div');
          palette.className = 'pr-bed-designer__palette';
          const summary = document.createElement('button');
          summary.type = 'button'; summary.className = 'pr-bed-designer__palette-label';
          summary.dataset.paletteLabel = ''; summary.dataset.openPalette = '';
          palette.append(summary);
          const colors = document.createElement('div');
          colors.className = 'pr-bed-designer__swatches'; colors.setAttribute('role', 'group');
          colors.setAttribute('aria-label', `${chosen[1].name} colors`);
          choices.forEach(([key, item]) => {
            const button = document.createElement('button');
            button.type = 'button'; button.className = 'pr-bed-designer__swatch';
            button.dataset.color = key; button.dataset.slot = slot;
            button.dataset.colorLabel = item.color || item.detail;
            const photo = document.createElement('img'); photo.src = item.thumb; photo.alt = ''; photo.width = 36; photo.height = 36; photo.loading = 'lazy';
            const colorText = document.createElement('span'); colorText.dataset.colorText = ''; colorText.className = 'pr-bed-designer__sr-only'; colorText.textContent = button.dataset.colorLabel;
            button.append(photo, colorText); button.title = button.dataset.colorLabel;
            button.setAttribute('aria-label', `${item.name}, ${item.color || item.detail}`);
            colors.append(button);
          });
          palette.append(colors);
          card.append(palette);
        }
        list.append(card);
      });
      const searchLabel = document.createElement('label');
      searchLabel.className = 'pr-bed-designer__search';
      const searchText = document.createElement('span');
      searchText.className = 'pr-bed-designer__search-label';
      searchText.textContent = 'Find a Collection or Color';
      const search = document.createElement('input');
      search.type = 'search';
      search.dataset.search = slot;
      search.placeholder = 'Try Stella, canvas, blue…';
      search.value = this.searches[slot] || '';
      search.autocomplete = 'off';
      searchLabel.append(searchText, search);
      const count = document.createElement('p');
      count.className = 'pr-bed-designer__status';
      count.dataset.resultCount = '';
      count.setAttribute('role', 'status');
      const more = document.createElement('button');
      more.type = 'button';
      more.className = 'pr-bed-designer__text-button';
      more.dataset.more = slot;
      more.textContent = 'Explore More Styles';
      const empty = document.createElement('p');
      empty.className = 'pr-bed-designer__status';
      empty.dataset.empty = '';
      empty.textContent = 'No styles match. Try another collection, color or style filter.';
      const fewer = document.createElement('button');
      fewer.type = 'button';
      fewer.className = 'pr-bed-designer__text-button';
      fewer.dataset.fewer = slot;
      fewer.textContent = 'Show Fewer Styles';
      const layerTop = document.createElement('div'); layerTop.className='pr-bed-designer__layer-top'; layerTop.append(choice);
      li.append(layerTop, searchLabel, filters);
      if (kind === 'cover') {
        const typeLabel = document.createElement('label'); typeLabel.className = 'pr-bed-designer__type-label';
        const text = document.createElement('span'); text.textContent = 'Piece Type';
        const select = document.createElement('select'); select.dataset.typeFilter = slot;
        const types = ['All Types', ...new Set([...families.values()].map((entries) => entries[0][1].typeGroup).filter(Boolean))];
        types.forEach((type) => { const option = document.createElement('option'); option.value = type; option.textContent = type; select.append(option); });
        select.value = this.types[slot] || 'All Types'; typeLabel.append(text, select); li.append(typeLabel);
      }
      const tools = document.createElement('div'); tools.className='pr-bed-designer__browse-tools';
      const typeControl=li.querySelector('.pr-bed-designer__type-label'); if(typeControl) tools.append(typeControl);
      const sortLabel = document.createElement('label'); sortLabel.className = 'pr-bed-designer__type-label';
      const sortText = document.createElement('span'); sortText.textContent = 'Sort By';
      const sort = document.createElement('select'); sort.dataset.sort = slot;
      [['best','Best Sellers'],['price-low','Price: Low to High'],['price-high','Price: High to Low'],['name','Collection Name']].forEach(([value,label]) => { const option = document.createElement('option'); option.value=value; option.textContent=label; sort.append(option); });
      sort.value=this.sorts[slot] || 'best'; sortLabel.append(sortText,sort); tools.append(sortLabel); li.append(tools);
      li.append(count, list, empty, more, fewer);
      if (optional) {
        const skip = document.createElement('button');
        skip.type = 'button';
        skip.className = 'pr-bed-designer__text-button';
        skip.dataset.skip = slot;
        skip.textContent = 'Skip This Layer';
        layerTop.append(skip);
      }
      steps.append(li);
    });
    this.syncSteps();
  }

  optionButton(slot, key, item) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'pr-bed-designer__option';
    button.dataset.slot = slot;
    this.fillOption(button, key, item);
    return button;
  }

  fillOption(button, key, item) {
    button.dataset.key = key;
    const v = this.size === 'king' ? item.king : item.queen;
    const variantLabel = v?.title.replace('Default Title', '') || '';
    button.setAttribute('aria-label', `${item.name}${variantLabel ? ', ' + variantLabel : ''}`);
    button.replaceChildren();
    const thumb = document.createElement('span');
    thumb.className = 'pr-bed-designer__option-thumb';
    const img = document.createElement('img');
    img.src = item.thumb;
    img.alt = '';
    img.loading = 'lazy';
    img.width = 480;
    img.height = 480;
    thumb.append(img);
    const selected = document.createElement('span'); selected.className = 'pr-bed-designer__selected'; selected.textContent = 'In Your Look'; selected.setAttribute('aria-hidden', 'true'); thumb.append(selected);
    const name = document.createElement('span');
    name.className = 'pr-bed-designer__option-name';
    name.textContent = item.name;
    const detail = document.createElement('span');
    detail.className = 'pr-bed-designer__option-detail';
    const forms = this.formsFor(key);
    const variants = forms.map(([, option]) => option[this.size]).filter(Boolean);
    const inStock = variants.filter((variant) => variant.available);
    const prices = (inStock.length ? inStock : variants).map((variant) => variant.price);
    const price = prices.length ? `${forms.length > 1 ? 'From ' : ''}${this.money(Math.min(...prices))}` : '';
    detail.textContent = [item.typeGroup || '', item.color || '', price, this.stockNote(key)].filter(Boolean).join(' · ');
    button.append(thumb, name, detail);
  }

  showScreen(index, focus = true) {
    this.screen=Math.max(0,Math.min(9,index));this.dataset.screen=String(this.screen);this.visited=Math.max(this.visited,this.screen);
    this.$('[data-welcome]').hidden=this.screen!==0;
    this.$('[data-room-step]').hidden=this.screen!==1;
    this.$('[data-review]').hidden=this.screen!==8;
    this.$('[data-steps]').hidden=this.screen<2||this.screen>7;
    this.querySelectorAll('[data-step]').forEach((el,i)=>{el.hidden=this.screen!==i+2;});
    this.$('[data-result]').hidden=this.screen!==9;this.$('[data-wizard]').hidden=this.screen===9;
    this.$('[data-design]').hidden=this.screen!==8;this.$('[data-next]').hidden=this.screen===8;this.$('[data-back]').hidden=this.screen===0;
    const step=PR_BD_SLOTS[this.screen-2];
    this.$('[data-screen-title]').textContent=this.screen===8?'Your Layers, Together':step?.title||'Your Bed Is Ready';
    this.$('[data-screen-help]').textContent=this.screen===8?'Take one last look at your choices. Then see your bed styled with these pieces.':step?.help||'';
    this.$('[data-step-count]').textContent=this.screen===8?'Review Your Bed':`Step ${this.screen+1} of 8 · ${this.screen===0?'Bed Size':this.screen===1?'Room':step?.label||''}`;
    this.$('[data-step-progress]').style.width=`${Math.min(100,(this.screen+1)/9*100)}%`;
    this.$('[data-next]').textContent=this.screen===1?'Choose Your Bedding':this.screen===7?'Review My Bed':'Continue';
    if(this.screen===8)this.buildReview();this.syncSteps();
    if(focus){
      const title=this.screen===9?this.$('[data-result-title]'):this.screen===0?this.$('[data-size-title]'):this.screen===1?this.$('[data-room-title]'):this.$('[data-screen-title]');
      title.focus({preventScroll:true});
      const anchor=this.screen===9?title:this.screen===0?this.$('.pr-bed-designer__masthead'):this.$('.pr-bed-designer__progress-head');
      const header=Math.max(0,...[...document.querySelectorAll('#custom--header-desktop,#custom--header-mobile')].map(el=>el.getBoundingClientRect().height));
      // Step changes must bypass the storefront's root scroll-behavior: smooth.
      window.scrollTo({top:Math.max(0,window.scrollY+anchor.getBoundingClientRect().top-header-24),behavior:'instant'});
    }
  }

  buildReview() {
    const list = this.$('[data-review-list]');
    list.replaceChildren();
    [{ label: 'Bed Size', value: this.size === 'king' ? 'King' : 'Queen', index: 0 }, {label:'Room',value:this.rooms.find(r=>r.id===this.room).name,index:1}, ...PR_BD_SLOTS.map((step, i) => ({ ...step, index: i + 2, item: this.items[this.sel[step.slot]] }))].filter((entry) => entry.value || entry.item).forEach(({ label, value, item, index }) => {
      const li = document.createElement('li');
      li.className = 'pr-bed-designer__review-row';
      if (index === 0) {
        const bed = this.$(`[data-size-choice="${this.size}"] svg`).cloneNode(true); li.append(bed); li.dataset.reviewSize = '';
      }
      if(index===1){const img=document.createElement('img');img.src=this.$('[data-room-preview]').src;img.alt='';li.append(img);}
      if (item) {
        const img = document.createElement('img'); img.src = item.thumb; img.alt = ''; img.width = 72; img.height = 72; li.append(img);
      }
      const text = document.createElement('div');
      const role = document.createElement('p'); role.className = 'pr-bed-designer__piece-role'; role.textContent = label;
      const name = document.createElement('p'); name.className = 'pr-bed-designer__piece-name'; name.textContent = value || (item ? `${item.name}${item.color ? ' · ' + item.color : ''}` : 'Skipped');
      text.append(role, name);
      const edit = document.createElement('button'); edit.type = 'button'; edit.className = 'pr-bed-designer__text-button'; edit.dataset.goto = index; edit.textContent = 'Edit'; edit.setAttribute('aria-label', `Edit ${label}`);
      li.append(text, edit); list.append(li);
    });
  }

  available(key) {
    const item = this.items[key];
    if (!item) return false;
    const variant = this.size === 'king' ? item.king : item.queen;
    return !!variant?.available;
  }

  syncSteps() {
    const busy = this.dataset.state === 'rendering' || this.photoChecking || this.resetting || this.restoringLook || this.adding;
    this.querySelectorAll('.pr-bed-designer__option, [data-color]').forEach((button) => {
      const slot = button.dataset.slot;
      const key = button.dataset.key || button.dataset.color;
      const chosen = this.sel[slot] === key;
      button.setAttribute('aria-pressed', String(chosen));
      const other = slot === 'base' ? this.sel.top : slot === 'top' ? this.sel.base : null;
      button.disabled = busy || this.sameVisual(other, key);
      if (button.dataset.color) {
        const variant = this.size === 'king' ? this.items[key].king : this.items[key].queen;
        const stock = this.stockNote(key);
        const note = this.sameVisual(other, key) ? ' · Already Used' : stock ? ` · ${stock}` : '';
        button.querySelector('[data-color-text]').textContent = button.dataset.colorLabel + note;
        button.setAttribute('aria-label', `${this.items[key].name}, ${button.dataset.colorLabel}${note}`);
      }
    });
    this.querySelectorAll('[data-step]').forEach((step) => {
      const slot = step.dataset.step;
      const item = this.items[this.sel[slot]];
      step.querySelector('[data-choice]').textContent = item ? `Selected: ${item.name}${item.color ? ' · ' + item.color : ''}${this.stockNote(this.sel[slot]) ? ' · ' + this.stockNote(this.sel[slot]) : ''}` : 'No layer selected';
      const filter = this.filters[slot] || 'All Designs';
      step.querySelectorAll('[data-filter]').forEach((button) => { button.setAttribute('aria-pressed', String(button.dataset.filter === filter)); });
      const words = (this.searches[slot] || '').trim().toLowerCase().split(/\s+/).filter(Boolean);
      const limit = Infinity;
      let matchesCount = 0;
      let shown = 0;
      const cards=[...step.querySelectorAll('[data-family]')];
      const sort=this.sorts[slot] || 'best';
      const price=card=>{const values=this.formsFor(card.querySelector('[data-key]').dataset.key).map(([,item])=>item[this.size]?.price).filter(value=>Number.isFinite(value));return values.length?Math.min(...values):Infinity;};
      cards.sort((a,b)=>{if(sort==='price-low'||sort==='price-high'){const ap=price(a),bp=price(b);if(!Number.isFinite(ap))return Number.isFinite(bp)?1:0;if(!Number.isFinite(bp))return -1;return sort==='price-low'?ap-bp:bp-ap;}if(sort==='name')return this.items[a.querySelector('[data-key]').dataset.key].name.localeCompare(this.items[b.querySelector('[data-key]').dataset.key].name);return Number(a.dataset.order)-Number(b.dataset.order);});
      step.querySelector('.pr-bed-designer__options').append(...cards);
      cards.forEach((card) => {
        const option = card.querySelector('[data-key]');
        const key = option.dataset.key;
        const keys = [key, ...[...card.querySelectorAll('[data-color]')].map((b) => b.dataset.color)];
        const matches = (filter === 'All Designs' || card.dataset.group === filter) && (!this.types[slot] || this.types[slot] === 'All Types' || card.dataset.typeGroup === this.types[slot]) && words.every((word) => card.dataset.searchText.includes(word));
        const selected = keys.includes(this.sel[slot]);
        if (matches) matchesCount++;
        card.hidden = !matches;
        if (!card.hidden) shown++;
        const summary = card.querySelector('[data-palette-label]');
        if (summary) summary.textContent = `${this.items[key].color || this.items[key].detail} · ${card.querySelectorAll('[data-color]').length} Colors ↗`;
        card.dataset.colorCount = String(card.querySelectorAll('[data-color]').length);
      });
      step.querySelector('[data-more]').hidden = matchesCount <= limit;
      step.querySelector('[data-more]').textContent = `Show ${Math.min(12, Math.max(0, matchesCount - limit))} More Styles`;
      step.querySelector('[data-fewer]').hidden = true;
      step.querySelector('[data-result-count]').textContent = `${matchesCount} ${matchesCount===1?'style':'styles'} · All styles shown${sort==='best'?' · Best sellers first':''}`;
      step.querySelector('[data-empty]').hidden = matchesCount > 0;
    });
    this.querySelectorAll('[data-start-over]').forEach(button=>{button.disabled=busy||!this.catalogReady||this.restoringLook||this.resetting;});
    this.$('[data-next]').disabled = busy || (this.screen===1&&(!this.catalogReady||this.rooms?.find(r=>r.id===this.room)?.readOnly||(this.roomMode||'preset')==='upload'&&!this.rooms?.find(r=>r.id===this.room)?.private)) || (this.screen === 2 && !this.items[this.sel.base]);
    this.$('[data-design]').disabled = busy || !this.items[this.sel.base];
    this.$('[data-shop-choices]').disabled = busy || !this.items[this.sel.base];
    this.querySelectorAll('[data-back], [data-skip], [data-goto], [data-edit-bed], [data-size-choice]').forEach((button) => { button.disabled = busy; });
    const unavailable = PR_BD_SLOTS.filter(({slot}) => this.sel[slot] && this.stockNote(this.sel[slot])).map(({label}) => label);
    const note = this.$('[data-stock-note]'); note.hidden = !unavailable.length;
    note.textContent = unavailable.length ? `Preview-only pieces: ${unavailable.join(', ')}. You can design with them, but unavailable pieces cannot be added to your cart.` : '';
    this.syncShareActions(busy);
    this.syncRetryAction(busy);
    this.syncStudio();
    this.syncRooms();
    this.saveDraft();
  }

  syncRetryAction(busy=false) {
    const button=this.$('[data-retry-preview]');if(!button)return;
    const shared=this.rooms?.find(r=>r.id===this.room)?.readOnly;
    button.hidden=!!shared||!(this.shoppingOnly||this.renderQuality==='approximate');
    button.disabled=!!busy;
    button.textContent=this.shoppingOnly?'Try Again':'Try Another Preview';
  }

  // ---------- stage ----------
  showOverlay(text, progress) {
    this.$('[data-overlay]').hidden = false;
    const room=this.rooms?.find(room=>room.id===this.room);if(room)this.$('[data-waiting-room]').src=this.regeneratingWithPreview?(this.renderBlob||`${this.api}${this.renderImage}`):this.roomURL(room);
    this.$('[data-overlay-text]').textContent = text;
    this.$('[data-bar]').hidden = !progress;
    this.$('.pr-bed-designer__render-phases').hidden=!progress;
  }

  hideOverlay() {
    this.$('[data-overlay]').hidden = true;
  }

  startProgress() { this.setRenderPhase('references'); }
  setRenderPhase(phase) {
    const phases=['references','render','checking'];const index=phase==='refining'?1:phases.indexOf(phase);if(index<0)return;
    this.$('[data-bar] span').style.width=`${(index+1)/3*100}%`;
    this.$('[data-overlay-text]').textContent=phase==='refining'?'This is taking a little longer. We’re making another preview to better match your selected pieces.':['Preparing your chosen pieces…','Styling your bed. This usually takes about a minute.','Checking the colors, patterns and layers…'][index];
    this.querySelectorAll('[data-render-phase]').forEach((el,i)=>{el.dataset.active=String(i===index);el.dataset.complete=String(i<index);});
  }
  stopProgress() { this.$('[data-bar] span').style.width='100%'; }

  // ---------- Turnstile ----------
  verificationError(reason, code = '') {
    // Keep only a documented numeric code. Never log tokens, visitor details or keys.
    const safeCode = /^\d{6}$/.test(String(code)) ? String(code) : '';
    this.dataset.verificationReason = reason;
    this.dataset.verificationCode = safeCode;
    console.info('bed_designer_verification', { reason, code: safeCode });
    const retryAction=this.photoChecking?'Use This Photo':'Design My Bed';
    const error = new Error(`The security check could not finish. Your choices are saved. Select ${retryAction} to try again.`);
    error.verification = true; error.reason = reason; error.code = safeCode;
    if (['110100','110110','110200','400020','400021','400070'].includes(safeCode)) {
      error.message = 'The preview security check is not configured correctly. Your choices are saved, and you can still shop them without a preview.';
      error.retryable = false;
    } else if (safeCode === '200100') {
      error.message = 'The security check could not confirm this browser. Check your device date and time, then try again. Your choices are saved.';
      error.retryable = false;
    } else if (reason === 'script') {
      error.message = `The security check could not connect. Select ${retryAction} to retry. If it keeps happening, allow challenges.cloudflare.com in your browser or open this page in another browser. Your choices are saved.`;
      error.retryable = true;
    } else error.retryable = reason !== 'interaction-timeout';
    return error;
  }

  loadTurnstile() {
    if (window.turnstile?.render) return Promise.resolve();
    if (this.turnstileScript) return this.turnstileScript;
    const pending = new Promise((resolve, reject) => {
      const script = document.createElement('script'); let settled = false;
      const finish = (ok) => {
        if (settled) return; settled = true; clearTimeout(timer);
        script.onload = script.onerror = null;
        if (ok && window.turnstile?.render) resolve();
        else { script.remove(); reject(this.verificationError('script')); }
      };
      const timer = setTimeout(() => finish(false), 20000);
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      script.async = true;
      script.onload = () => finish(true);
      script.onerror = () => finish(false);
      document.head.append(script);
    });
    this.turnstileScript = pending;
    pending.catch(() => { if (this.turnstileScript === pending) this.turnstileScript = null; });
    return pending;
  }

  getToken() {
    if (this.verificationPromise) return this.verificationPromise;
    const pending = this.obtainToken(); this.verificationPromise = pending;
    const clear = () => { if (this.verificationPromise === pending) this.verificationPromise = null; };
    pending.then(clear, clear);
    return pending;
  }

  async obtainToken() {
    for (let attempt = 0; attempt < 2; attempt++) {
      try { return await this.tokenAttempt(); }
      catch (error) {
        if (attempt || !error.verification || !error.retryable) throw error;
        this.showVerification('Reconnecting the security check. Your choices are safe…');
        await new Promise(resolve => setTimeout(resolve, 750));
      }
    }
  }

  async tokenAttempt() {
    await this.loadTurnstile();
    const slot = this.verificationSlot || this.$('[data-turnstile-slot]'); slot.hidden = false;
    let timer, widget, active = true, interactive = false;
    const pending = new Promise((resolve, reject) => {
      const fail = (reason, code) => { if (active) { active = false; reject(this.verificationError(reason, code)); } };
      const deadline = (ms) => { clearTimeout(timer); timer = setTimeout(() => fail(interactive ? 'interaction-timeout' : 'timeout'), ms); };
      deadline(30000);
      try {
        widget = window.turnstile.render(slot, {
          sitekey: this.dataset.turnstile,
          appearance: 'interaction-only', size: 'compact', action: 'bed-design',
          retry: 'never', 'refresh-expired': 'never', 'refresh-timeout': 'never',
          callback: (token) => {
            if (!active) return; active = false;
            if (typeof token !== 'string' || !token) { reject(this.verificationError('empty')); return; }
            this.dataset.verificationReason = 'passed'; this.dataset.verificationCode = '';
            resolve(token);
          },
          'before-interactive-callback': () => {
            if (!active) return; interactive = true; deadline(120000);
            this.showVerification('Complete the quick security check below to continue.');
          },
          'error-callback': (code) => { fail('challenge', code); return true; },
          'expired-callback': () => fail('expired'),
          'timeout-callback': () => fail('interaction-timeout'),
        });
      } catch { fail('widget'); }
    });
    try { return await pending; }
    finally {
      active = false; clearTimeout(timer);
      if (widget != null) { try { window.turnstile.remove(widget); } catch {} }
      slot.hidden = true;
    }
  }

  expectedRecipe(sel) {
    const room=this.rooms.find(room=>room.id===this.room);if(room?.readOnly)return room.recipe;
    const revisions=Object.keys(sel).filter(slot=>sel[slot]).sort().map(slot=>this.items[sel[slot]]?.referenceRevision);
    return (room?.recipe||'fidelity-v1')+(room?.private&&room.size==='king'&&sel.euro?'-king-euros-v2':'')+(Object.values(sel).some(key=>this.items[key]?.stitchPattern==='channel')?'-channels-v1':'')+(Object.values(sel).some(key=>this.items[key]?.shot2Extra)?'-shot2-extra-v1':'')+(Object.values(sel).some(key=>this.items[key]?.toilePattern)?'-desert-toile-v1':'')+(revisions.some(Boolean)?'-refs-'+revisions.map(r=>r||'legacy').join('.'):'');
  }

  // ---------- design ----------
  async design(userAction, regenerate=false) {
    if (!this.items[this.sel.base] || this.dataset.state === 'rendering') return;
    const sel = { ...this.sel };
    const retryKey=this.room+'|'+this.comboKey(sel);
    if(regenerate&&this.retryUntil>Date.now()&&this.retryDesign===retryKey){this.setStatus('[data-retry-status]','You have already tried another preview for this look. Keep your current result, edit your look, or try again in a few minutes.','error');return;}
    const keep=regenerate&&!this.shoppingOnly&&this.renderImage&&this.room===this.renderedRoom&&this.comboKey(sel)===this.comboKey(this.rendered);
    const previousImageSrc=keep?this.$('[data-stage-image]').src:null;
    const purchaseChoices=regenerate?[...this.querySelectorAll('[data-piece]')].map(row=>({slot:row.dataset.slot,key:row.dataset.purchaseKey,qty:row.querySelector('[data-qty]').textContent,overridden:row.dataset.qtyOverridden})):[];
    let attempt=null;
    if(regenerate){if(this.pendingRetry?.design!==retryKey)this.pendingRetry={design:retryKey,retryId:[...crypto.getRandomValues(new Uint8Array(16))].map(v=>v.toString(16).padStart(2,'0')).join(''),...(keep?{previous:this.renderImage.match(/([a-f0-9]{64})\.jpg$/)?.[1]}:{})};attempt=this.pendingRetry;}
    this.regeneratingWithPreview=!!keep;this.setStatus('[data-retry-status]','');
    this.setQualityNote();
    this.shoppingOnly=false; this.$('[data-result]').dataset.shoppingOnly='false'; this.$('.pr-bed-designer__stage').hidden=false;
    if (!regenerate && this.rendered && this.room === this.renderedRoom && this.comboKey(sel) === this.comboKey(this.rendered) && this.renderRecipe === this.expectedRecipe(sel)) {
      this.$('[data-cart]').hidden = false; this.$('[data-result-title]').textContent = this.renderQuality==='approximate' ? 'Your Styling Preview' : 'Your Bed Is Ready';
      this.setQualityNote(this.renderQuality,this.renderWarnings); this.updateShareLinks(); this.showScreen(9); return;
    }
    if(this.rooms?.find(r=>r.id===this.room)?.readOnly){this.setStatus('[data-status]','Choose one of our rooms or upload your own to restyle this shared look.','error');this.showScreen(1);return;}
    if(!keep){this.publicShareId=null;this.$('[data-share-consent]').checked=false;}
    this.dataset.state = 'rendering';this.$('[data-stage-image]').hidden=true;
    this.syncSteps();
    this.showScreen(9);
    this.$('[data-result-title]').textContent = 'Styling Your Bed';
    this.$('[data-cart]').hidden = true;
    this.setStatus('[data-status]', '');
    if (userAction) {
      this.showOverlay('Checking a few things first…', false);
      const stage = this.$('.pr-bed-designer__stage');
      if (stage && stage.getBoundingClientRect().top < 0) stage.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
    }
    try {
      let result = await this.requestRender(sel, null, false, attempt);
      if (result.status === 'verify') {
        if(this.room?.startsWith('upload-')){this.showOverlay('Preparing your room and chosen pieces…',false);const ready=await this.requestRender(sel,null,true,attempt);if(ready.status!=='prepared')throw Error(ready.message||'Your room could not be prepared. Please try again in a moment.');}
        const token = await this.getToken();
        this.showOverlay('Styling and checking your bed. This usually takes about a minute.', true);
        this.startProgress();
        result = await this.requestRender(sel, token, false, attempt);
      }
      this.stopProgress();
      if(regenerate&&['done','quality'].includes(result.status)){this.retryUntil=Date.now()+600000;this.retryDesign=retryKey;this.pendingRetry=null;}
      if(keep&&(result.preserved||result.status==='quality')){this.dataset.state='ready';this.hideOverlay();this.$('[data-stage-image]').hidden=false;this.$('[data-cart]').hidden=false;this.setQualityNote(this.renderQuality,this.renderWarnings);this.$('[data-result-title]').textContent='Your Styling Preview';this.setStatus('[data-retry-status]',result.preserved?result.message:'We could not finish another preview this time. Your earlier preview is still here.');this.showScreen(9);return;}
      if (result.status === 'quality') { this.showSelectionBoard(result.message); return; }
      if (result.status !== 'done') throw new Error(result.message || 'We could not design this bed just now. Please try again in a minute.');
      await this.showImage(`${this.api}${result.image}`);
      this.setQualityNote(result.quality,result.warnings); this.renderQuality=result.quality; this.renderWarnings=result.warnings;
      this.publicShareId=null;this.$('[data-share-consent]').checked=false;
      this.renderImage = result.image; this.renderRecipe = result.recipe || 'fidelity-v1'; this.renderedRoom = result.room || this.room;
      this.rendered = result.slots || sel;
      this.dataset.state = 'ready';
      this.hideOverlay();
      this.buildCart();
      if(regenerate){this.querySelectorAll('[data-piece]').forEach(row=>{const prior=purchaseChoices.find(p=>p.slot===row.dataset.slot);if(!prior)return;if(this.formsFor(row.dataset.piece).some(([key])=>key===prior.key)){row.dataset.purchaseKey=prior.key;const control=row.querySelector('[data-purchase-format]');if(control)control.value=prior.key;}row.querySelector('[data-qty]').textContent=prior.qty;if(prior.overridden)row.dataset.qtyOverridden=prior.overridden;});this.applySize();}
      this.updateShareLinks();
      const savedURL=this.savedLookURL();if(savedURL)window.history.replaceState(null,'',savedURL);
      this.$('[data-result-title]').textContent = this.renderQuality==='approximate' ? 'Your Styling Preview' : 'Your Bed Is Ready';
      this.showScreen(9);
    } catch (error) {
      this.stopProgress();
      this.dataset.state = this.rendered ? 'changed' : 'loading';
      this.hideOverlay();
      if(keep){this.dataset.state='ready';this.$('[data-stage-image]').src=previousImageSrc;this.$('[data-stage-image]').hidden=false;this.$('[data-cart]').hidden=false;this.setQualityNote(this.renderQuality,this.renderWarnings);this.$('[data-result-title]').textContent='Your Styling Preview';this.showScreen(9);this.setStatus('[data-retry-status]',error.message+' Your earlier preview is still here.','error');}
      else{this.showScreen(8);this.setStatus('[data-status]', error.message, 'error');}
    } finally {
      this.regeneratingWithPreview=false;this.syncSteps();
    }
  }

  async requestRender(sel, token, prepareOnly=false, attempt=null) {
    const response = await fetch(`${this.api}/render`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ slots: sel, token, room: this.room, brand: this.brand,...(attempt?{retryId:attempt.retryId,...(attempt.previous?{previous:attempt.previous}:{})}:{}),...(prepareOnly?{prepareOnly:true}:{}),...(this.room.startsWith('upload-')?{roomKey:this.ownedRoom?.key}:{}) }),
    });
    const type = response.headers.get('Content-Type') || '';
    if (!type.includes('ndjson')) return response.json().catch(() => ({ status: 'error' }));
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let last = { status: 'error' };
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop();
      for (const line of lines) {
        if (!line.trim()) continue;
        try {
          last = JSON.parse(line);if(last.phase)this.setRenderPhase(last.phase);
        } catch (error) {
          /* partial line */
        }
      }
    }
    if (buffer.trim()) {
      try {
        last = JSON.parse(buffer);
      } catch (error) {
        /* ignore */
      }
    }
    return last;
  }

  setQualityNote(quality, warnings=[]) {
    const note=this.$('[data-quality-note]');
    note.hidden=quality!=='approximate';
    note.textContent=quality==='approximate' ? 'Styling Preview · '+(warnings?.length ? warnings.join(' ') : 'Some product details may differ. Check the product photos below before purchasing.') : '';
  }

  showSelectionBoard(message='') {
    this.shoppingOnly=true; this.dataset.state='ready'; this.hideOverlay();
    this.$('[data-result]').dataset.shoppingOnly='true'; this.$('.pr-bed-designer__stage').hidden=true;
    this.buildCart(this.sel); this.$('[data-result-title]').textContent='Your Selected Layers';
    const note=this.$('[data-quality-note]');note.hidden=false;
    note.textContent=message || 'Your pieces are ready below. We could not create a useful room preview this time, so we’re showing the actual product photos.';
    this.showScreen(9);
  }

  async showImage(src) {
    const previousBlob=this.renderBlob;let candidateBlob=null;
    if(/\/private\//.test(src)){const response=await fetch(src,{headers:this.privateHeaders(src)});if(!response.ok)throw Error('This private preview is unavailable or expired.');candidateBlob=URL.createObjectURL(await response.blob());src=candidateBlob;}
    return new Promise((resolve,reject)=>{
      const img=this.$('[data-stage-image]');
      img.onload=()=>{if(candidateBlob){this.renderBlob=candidateBlob;if(previousBlob)URL.revokeObjectURL(previousBlob);}img.hidden=false;resolve();};
      img.onerror=()=>{if(candidateBlob)URL.revokeObjectURL(candidateBlob);reject(Error('Your bed image could not load. Please try again.'));};
      img.src=src;
    });
  }

  // ---------- cart ----------
  buildCart(slots=this.rendered) {
    this.shoppingSlots={...slots};
    const list = this.$('[data-pieces]');
    list.innerHTML = '';
    PR_BD_SLOTS.filter(({ slot }) => slots[slot]).forEach(({ slot, label, qty }) => {
      const key = slots[slot];
      const item = this.items[key];
      if (!item) return;
      const li = document.createElement('li');
      li.className = 'pr-bed-designer__piece';
      li.dataset.piece = key;
      li.dataset.slot = slot;
      const forms = this.formsFor(key);
      li.dataset.purchaseKey = forms.find(([candidate]) => this.available(candidate))?.[0] || key;
      li.innerHTML = `
        <a class="pr-bed-designer__piece-thumb" tabindex="-1" aria-hidden="true"><img alt="" loading="lazy" width="56" height="56"></a>
        <div class="pr-bed-designer__piece-info">
          <p class="pr-bed-designer__piece-role"></p>
          <a class="pr-bed-designer__piece-name"></a>
          <p class="pr-bed-designer__piece-variant"></p>
          <p class="pr-bed-designer__piece-soldout" hidden></p>
        </div>
        <div class="pr-bed-designer__piece-actions">
          <span class="pr-bed-designer__qty">
            <button type="button" data-qty-step="-1">&minus;</button>
            <output data-qty aria-live="polite">${qty}</output>
            <button type="button" data-qty-step="1">+</button>
          </span>
          <span class="pr-bed-designer__price" data-price></span>
          <button type="button" class="pr-bed-designer__add-piece" data-add-piece>Add</button>
        </div>`;
      li.querySelector('img').src = item.thumb;
      li.querySelector('.pr-bed-designer__piece-role').textContent = label;
      li.querySelector('.pr-bed-designer__piece-name').textContent = item.name;
      li.querySelector('[data-qty-step="-1"]').setAttribute('aria-label', `One fewer ${item.name}`);
      li.querySelector('[data-qty-step="1"]').setAttribute('aria-label', `One more ${item.name}`);
      if (forms.length > 1) {
        const label = document.createElement('label'); label.className = 'pr-bed-designer__format-label';
        const title = document.createElement('span'); title.textContent = 'Purchase Format';
        const select = document.createElement('select'); select.dataset.purchaseFormat = '';
        forms.forEach(([candidate, option]) => { const el = document.createElement('option'); el.value = candidate; el.textContent = option.pieceType === 'duvet' ? 'Duvet Cover Set' : 'Comforter Set'; select.append(el); });
        select.value = li.dataset.purchaseKey; label.append(title, select); li.querySelector('.pr-bed-designer__piece-info').append(label);
      }
      list.append(li);
    });
    this.$('[data-cart]').hidden = false;
    this.setStatus('[data-cart-status]', '');
    this.applySize();
  }

  variantFor(key) {
    const item = this.items[key];
    return this.size === 'king' ? item?.king : item?.queen;
  }

  applySize() {
    this.dataset.size = this.size;
    this.querySelectorAll('[data-size-choice]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.sizeChoice === this.size)));
    let total = 0;
    let any = false;
    let anyUnavailable = false;
    this.querySelectorAll('[data-piece]').forEach((li) => {
      const key = li.dataset.purchaseKey || li.dataset.piece;
      const item = this.items[key];
      const v = this.variantFor(key);
      const quantity = li.querySelector('[data-qty]');
      // The standing styling standard uses two euros on Queen and three on King.
      // Preserve a shopper's manual quantity choice across size changes.
      if (li.dataset.slot === 'euro' && !li.dataset.qtyOverridden) quantity.textContent = this.size === 'king' ? '3' : '2';
      const qty = Number(quantity.textContent) || 1;
      const ok = !!v?.available;
      if (!ok) anyUnavailable = true;
      const link = li.querySelector('.pr-bed-designer__piece-name');
      const productVariant = v || item.referenceVariant;
      link.href = `/products/${item.handle}${productVariant ? `?variant=${productVariant.id}` : ''}`;
      li.querySelector('.pr-bed-designer__piece-thumb').href = link.href;
      li.querySelector('.pr-bed-designer__piece-variant').textContent = v ? v.title.replace('Default Title', '') : item.color || item.detail;
      const soldout = li.querySelector('.pr-bed-designer__piece-soldout');
      soldout.hidden = ok;
      soldout.textContent = this.unavailableLabel(key);
      li.querySelector('[data-price]').textContent = v ? this.money(v.price * qty) : '';
      li.querySelectorAll('[data-add-piece], [data-qty-step]').forEach((c) => { c.disabled = !ok || !!this.adding; });
      if (ok) { total += v.price * qty; any = true; }
    });
    const insertTotal=this.syncInserts();total+=insertTotal;if(insertTotal>0)any=true;
    const whole = this.$('[data-add-bed]');
    whole.disabled = !any || !!this.adding;
    this.querySelectorAll('[data-purchase-format]').forEach(control=>{control.disabled=!!this.adding;});
    const addLabel = anyUnavailable ? 'Add Available Pieces' : 'Add the Whole Bed';
    whole.textContent = any ? `${addLabel} · ${this.money(total)}` : addLabel;
    if (Object.keys(this.items).length) this.syncSteps();
    if (this.renderImage) this.updateShareLinks();
  }

  insertPlan() {
    const plan={};
    this.querySelectorAll('[data-piece]').forEach(row=>{
      const key=row.dataset.purchaseKey||row.dataset.piece,item=this.items[key],v=this.variantFor(key);if(!item||!v)return;
      const qty=Math.max(1,Number(row.querySelector('[data-qty]').textContent)||1);
      if(item.pieceType==='duvet')plan.duvet=(plan.duvet||0)+qty;
      if(item.pieceType==='bedspread')plan.bedspread=(plan.bedspread||0)+qty;
      if(row.dataset.slot==='euro'&&item.needsEuroInsert)plan.euro=(plan.euro||0)+qty;
      if(['base','top'].includes(row.dataset.slot)&&item.shams&&!/only/i.test(v.title)){const kind=/standard\s+shams?/i.test(v.title)?'pillow-standard':/king\s+shams?/i.test(v.title)?'pillow-king':'pillow';plan[kind]=(plan[kind]||0)+item.shams*qty;}
    });
    if(!Object.keys(plan).some(kind=>kind.startsWith('pillow'))&&this.shoppingSlots?.sheets&&this.variantFor(this.shoppingSlots.sheets))plan.pillow=2;
    return plan;
  }
  insertOptions(kind) {
    const sourceKind=kind.startsWith('pillow')?'pillow':kind;
    const size=kind==='pillow-standard'?'standard':kind==='pillow-king'?'king':this.size;
    return (this.inserts||[]).filter(p=>p.kind===sourceKind).flatMap(p=>p.variants.filter(v=>sourceKind==='euro'||(size==='king'?/king/i.test(v.title):sourceKind==='pillow'?/standard/i.test(v.title):/queen/i.test(v.title))).map(v=>({...v,name:p.name,handle:p.handle,thumb:p.thumb})));
  }

  syncInserts() {
    const root=this.$('[data-insert-options]');if(!root)return 0;
    const plan=this.insertPlan(),labels={duvet:'Duvet Insert',bedspread:'Bedspread Insert',euro:'Euro Pillow Inserts',pillow:'Pillow Inserts','pillow-standard':'Standard Pillow Inserts','pillow-king':'King Pillow Inserts'};
    const key=JSON.stringify({plan,size:this.size,inserts:(this.inserts||[]).map(p=>p.variants.map(v=>[v.id,v.available,v.price]))});
    if(this.insertPlanKey!==key){
      this.insertPlanKey=key;root.replaceChildren();
      Object.entries(plan).forEach(([kind,quantity])=>{
        const options=this.insertOptions(kind);if(!options.length)return;
        const card=document.createElement('div');card.className='pr-bed-designer__insert-card';card.dataset.insertKind=kind;card.dataset.quantity=quantity;
        const choice=this.insertChoices[kind]||{};const selected=options.find(v=>v.id===choice.id&&v.available)||options.find(v=>v.available)||options[0];this.insertChoices[kind]={...choice,id:selected.id,checked:!!choice.checked&&!!selected.available};
        const label=document.createElement('label');label.className='pr-bed-designer__piece-name';
        const check=document.createElement('input');check.type='checkbox';check.dataset.insertCheck=kind;check.setAttribute('aria-label',`Add ${labels[kind]}`);check.disabled=!selected.available;label.append(check,document.createTextNode(labels[kind]));
        const detail=document.createElement('p');detail.className='pr-bed-designer__disclosure';detail.textContent=`Recommended · ${quantity} ${quantity===1?'insert':'inserts'} for your selected bedding${kind.startsWith('pillow')?` · ${kind==='pillow-standard'?'Standard':kind==='pillow-king'?'King':this.size==='king'?'King':'Standard'}`:''}`;
        const select=document.createElement('select');select.className='pr-bed-designer__insert-select';select.dataset.insertVariant=kind;select.setAttribute('aria-label',`${labels[kind]} Fill`);
        options.forEach(v=>{const option=document.createElement('option');option.value=v.id;option.textContent=`${v.handle.includes('down-alternative')||v.handle==='bedspread-insert'?'Down Alternative':'Feather & Down'}${v.title==='Default Title'?'':' · '+v.title} · ${this.money(v.price)} Each${v.available?'':' · Sold Out'}`;option.disabled=!v.available;select.append(option);});select.value=selected.id;
        const wrapper=document.createElement('label');wrapper.className='pr-bed-designer__format-label';wrapper.append(document.createTextNode('Choose Your Fill'),select);
        const footer=document.createElement('p');footer.className='pr-bed-designer__price';footer.dataset.insertPrice='';
        card.append(label,detail,wrapper,footer);root.append(card);
      });
    }
    let total=0;
    root.querySelectorAll('[data-insert-kind]').forEach(card=>{
      const kind=card.dataset.insertKind,options=this.insertOptions(kind),choice=this.insertChoices[kind]||{},v=options.find(v=>v.id===choice.id),quantity=Number(card.dataset.quantity);
      const check=card.querySelector('[data-insert-check]');check.checked=!!choice.checked&&!!v?.available;check.disabled=!v?.available||!!this.adding;card.querySelector('select').disabled=!!this.adding;card.querySelector('select').value=choice.id;
      card.querySelector('[data-insert-price]').textContent=v?.available?`${check.checked?'Included in Total':'Optional'} · ${this.money(v.price*quantity)}`:'Matching inserts are sold out right now.';
      if(check.checked)total+=v.price*quantity;
    });
    this.$('[data-inserts]').hidden=!root.children.length;
    return total;
  }
  insertLines() {
    const plan=this.insertPlan();return Object.entries(this.insertChoices||{}).flatMap(([kind,choice])=>{
      const v=this.insertOptions(kind).find(v=>v.id===choice.id);if(!choice.checked||!plan[kind]||!v?.available)return [];
      return [{id:v.id,quantity:plan[kind],properties:{_bed_designer:this.comboKey(this.shoppingSlots||this.rendered),_bed_designer_insert:kind}}];
    });
  }

  lineFor(li) {
    const v = this.variantFor(li.dataset.purchaseKey || li.dataset.piece);
    if (!v?.available) return null;
    const quantity = Math.max(1, Number(li.querySelector('[data-qty]').textContent) || 1);
    return { id: v.id, quantity, properties: { _bed_designer: this.comboKey(this.shoppingSlots || this.rendered) } };
  }

  setStatus(selector, message, tone) {
    const el = this.$(selector);
    if (!el) return;
    el.textContent = message;
    if (tone) el.dataset.tone = tone;
    else delete el.dataset.tone;
  }

  async add(items, button) {
    if(this.adding||!items.length)return;
    this.adding=true;this.applySize();button.disabled=true;
    this.setStatus('[data-cart-status]', 'Adding to your cart…');
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.description || payload.message || 'We could not add these pieces. Please try again.');
      const units = items.reduce((sum, item) => sum + item.quantity, 0);
      this.setStatus('[data-cart-status]', `Added ${units} ${units === 1 ? 'piece' : 'pieces'} to your cart.`);
      await this.notifyCartAdded(payload,items);
    } catch (error) {
      this.setStatus('[data-cart-status]', error.message, 'error');
    } finally {
      this.adding=false;this.applySize();
    }
  }

  async notifyCartAdded(payload,items) {
    if (this.brand === 'hea') {
      // HEA's native controller differs from Paseo Road's event bridge.
      // A drawer failure after a successful add must never invite a duplicate add.
      if (window.HEACart?.refresh) {
        try { await window.HEACart.refresh(); window.HEACart.open?.(); return; } catch {}
      }
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh',{bubbles:true}));
      items.forEach(item=>document.dispatchEvent(new CustomEvent('product:added',{detail:{variant:{id:item.id},quantity:item.quantity}})));
      return;
    }
    this.dispatchEvent(new CustomEvent('cart:item-added',{bubbles:true,detail:{item:payload,cart:payload,source:`${this.brand}-bed-designer`}}));
  }

  // ---------- events ----------
  onClick(event) {
    if (this.dataset.state === 'rendering'||this.resetting||this.adding) return;
    if(event.target.closest('[data-retry-preview]')){this.design(true,true);return;}
    if(event.target.closest('[data-start-over]')){this.startOver();return;}
    if (event.target.closest('[data-shop-choices]')) {
      if(!this.items[this.sel.base]) return;
      this.setQualityNote(); this.shoppingOnly=true; this.$('[data-result]').dataset.shoppingOnly='true';
      this.$('.pr-bed-designer__stage').hidden=true; this.buildCart(this.sel);
      this.$('[data-result-title]').textContent='Shop Your Selected Layers'; this.showScreen(9); return;
    }
    if (event.target.closest('[data-close-palette]')) { this.$('[data-color-dialog]').close(); return; }
    const dialogColor = event.target.closest('[data-dialog-color]');
    if (dialogColor) { this.paletteKey = dialogColor.dataset.dialogColor; this.syncPalette(); return; }
    if (event.target.closest('[data-confirm-color]')) { this.choose(this.paletteSlot, this.paletteKey); this.$('[data-color-dialog]').close(); return; }
    const opener = event.target.closest('[data-open-palette]');
    if (opener) { this.openPalette(opener.closest('[data-family]')); return; }
    const roomChoice = event.target.closest('[data-room-choice]');
    if (roomChoice&&!roomChoice.disabled&&!this.photoChecking) { this.setupChanged=true; this.room = roomChoice.dataset.roomChoice;this.roomMode=this.rooms.find(r=>r.id===this.room)?.private?'upload':'preset'; this.syncRooms();this.syncSteps(); this.saveDraft(); return; }
    if (event.target.closest('[data-edit-room]')) { this.showScreen(1); return; }
    const roomMode=event.target.closest('[data-room-mode]');if(roomMode){this.setRoomMode(roomMode.dataset.roomMode);return;}
    if(event.target.closest('[data-upload-submit]')){this.uploadOwnRoom();return;}
    if(event.target.closest('[data-create-share]')){this.createPrivateShare();return;}
    if(event.target.closest('[data-delete-room]')){this.deletePrivateRoom();return;}
    if(this.photoChecking)return;
    if (event.target.closest('[data-save-image]')) { event.preventDefault(); this.saveImage(); return; }
    if (event.target.closest('[data-copy-look]')) { this.copyLook(); return; }
    const chapter = event.target.closest('[data-journey] button');
    if (chapter && !chapter.disabled) { this.showScreen(Number(chapter.dataset.journey)); return; }
    if (event.target.closest('[data-begin]')) { this.showScreen(1); return; }
    const nav = event.target.closest('[data-next], [data-back], [data-goto], [data-edit-bed]');
    if (nav) {
      if (nav.hasAttribute('data-goto')) this.showScreen(Number(nav.dataset.goto));
      else if (nav.hasAttribute('data-edit-bed')) this.showScreen(8);
      else this.showScreen(this.screen + (nav.hasAttribute('data-next') ? 1 : -1));
      return;
    }
    const filter = event.target.closest('[data-filter]');
    if (filter) { this.filters[filter.dataset.slot] = filter.dataset.filter; this.expanded[filter.dataset.slot] = 6; this.syncSteps(); return; }
    const more = event.target.closest('[data-more]');
    if (more) { this.expanded[more.dataset.more] = (this.expanded[more.dataset.more] || 6) + 12; this.syncSteps(); return; }
    const fewer = event.target.closest('[data-fewer]');
    if (fewer) { this.expanded[fewer.dataset.fewer] = 6; this.syncSteps(); return; }
    const skip = event.target.closest('[data-skip]');
    if (skip) { delete this.sel[skip.dataset.skip]; this.showScreen(this.screen + 1); return; }
    const color = event.target.closest('[data-color]');
    const option = event.target.closest('.pr-bed-designer__option');
    const choice = color || option;
    if (choice && !choice.disabled) {
      const slot = choice.dataset.slot;
      const key = choice.dataset.color || choice.dataset.key;
      if (option && option.closest('[data-family]').querySelectorAll('[data-color]').length > 1) { this.openPalette(option.closest('[data-family]')); return; }
      this.choose(slot, key);
      this.setStatus('[data-status]', '');
      this.syncSteps();
      return;
    }

    const sizeButton = event.target.closest('[data-size-choice]');
    if (sizeButton) {
      this.setupChanged=true;this.size = sizeButton.dataset.sizeChoice;
      try {
        localStorage.setItem(`${this.brand}-bed-designer-size`, this.size);
      } catch (error) {
        /* storage blocked */
      }
      this.renderSteps();
      this.applySize();
      this.showScreen(this.screen, false);
      return;
    }

    if (event.target.closest('[data-design]')) {
      this.design(true);
      return;
    }

    const step = event.target.closest('[data-qty-step]');
    if (step) {
      const piece = step.closest('[data-piece]');
      piece.dataset.qtyOverridden = '1';
      const out = piece.querySelector('[data-qty]');
      out.textContent = String(Math.min(10, Math.max(1, (Number(out.textContent) || 1) + Number(step.dataset.qtyStep))));
      this.applySize();
      return;
    }

    const addPiece = event.target.closest('[data-add-piece]');
    if (addPiece) {
      const line = this.lineFor(addPiece.closest('[data-piece]'));
      if (line) this.add([line], addPiece);
      return;
    }

    const addBed = event.target.closest('[data-add-bed]');
    if (addBed) {
      const lines = [...this.querySelectorAll('[data-piece]')].map((li) => this.lineFor(li)).filter(Boolean).concat(this.insertLines());
      if (lines.length) this.add(lines, addBed);
    }
  }
}

if (!customElements.get('pr-bed-designer')) {
  customElements.define('pr-bed-designer', PrBedDesigner);
}
